import * as coreCommon from 'assets/core/js/common'
import { PartialsEvents } from 'assets/core/js/module/partialsManager'
import noUiSlider from 'nouislider'
import type { AccommodationsData } from 'assets/core/js/types/globals'
import type { API, Options } from 'nouislider'

interface TargetElement extends HTMLElement {
  noUiSlider?: API
}

export default function (formId: string, synchronizeInput: boolean): void {
  const formEl = document.getElementById(formId)

  if (!formEl) {
    return
  }

  const budgetBlock = formEl.querySelector<HTMLElement>('.filter__block[data-filter="budgets"]')

  if (!budgetBlock) {
    return
  }

  const hiddenWrapper = budgetBlock.querySelector('.list__wrapper')

  if (!hiddenWrapper) {
    return
  }

  const input = hiddenWrapper.querySelector('input')

  if (!input) {
    return
  }

  const observer = new window.IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        initSlider(budgetBlock, input, synchronizeInput)
        observer.unobserve(entry.target)
      }
    })
  })

  observer.observe(budgetBlock)

  document.addEventListener(PartialsEvents.POST_UPDATE, (e: Event) => {
    const detail = (e as CustomEvent as { detail: { data: { accommodationsData: AccommodationsData } } }).detail
    if (!detail || !detail.data || !detail.data.accommodationsData) {
      return
    }

    updateSliderRange(document.querySelector<HTMLElement>('.budget-slider'), input, synchronizeInput, detail.data.accommodationsData)
  })
}

const initSlider = (budgetBlock: HTMLElement, input: HTMLInputElement, synchronizeInput: boolean): void => {
  if (!input.dataset.min || !input.dataset.max || !input.dataset.step) {
    return
  }

  const min = parseInt(input.dataset.min, 10)
  const max = parseInt(input.dataset.max, 10)
  const step = parseInt(input.dataset.step, 10)

  const wrapperEl = budgetBlock.querySelector<HTMLElement>('.budgets__wrapper')

  if (!wrapperEl) {
    return
  }

  const div = document.createElement('div')
  div.className = 'budget-slider'
  const sliderEl: TargetElement = wrapperEl.appendChild(div)

  const sliderConfig: Options = {
    range: {
      min,
      max,
    },
    step,
    tooltips: [true, true],
    start: [min, max],
    connect: true,
    format: {
      to: function (value: number) {
        return coreCommon.formatCurrency(value, undefined, true)
      },
      from: function (value: string) {
        return Number(value)
      },
    },
  }

  noUiSlider.create(sliderEl, sliderConfig)

  sliderEl.querySelectorAll<HTMLElement>('.noUi-handle').forEach((el) => {
    el.classList.add('noUi-handle-initial-position')
  })

  updateSliderRange(sliderEl, input, synchronizeInput, window.accommodationsData)

  const slider = sliderEl.noUiSlider

  if (!slider) {
    return
  }

  slider.on('set.one', (_values, _handle, unencoded, _tap, _positions, noUiSlider) => {
    const [from, to] = unencoded
    const { min, max } = noUiSlider.options.range

    if (!from || !to) {
      return
    }

    const apply = from !== min || to !== max

    input.value = `${from}:${to}`

    if (apply) {
      input.checked = true
    } else {
      input.checked = false
    }

    input.dispatchEvent(new Event('change'))
  })

  slider.on('slide', (_values, handle, _unencoded, _tap, positions, noUiSlider) => {
    const target = noUiSlider.target
    const handleEl = target.querySelector(`.noUi-handle-${handle === 1 ? 'upper' : 'lower'}`)
    const position = positions[handle]

    if (!handleEl || !position) {
      return
    }

    if ((handle === 0 && position >= 25) || (handle === 1 && position <= 75)) {
      handleEl.classList.remove('noUi-handle-initial-position')
    } else {
      handleEl.classList.add('noUi-handle-initial-position')
    }
  })
}

const updateSliderRange = (sliderEl: TargetElement | null, input: HTMLInputElement, synchronizeInput: boolean, data: AccommodationsData): void => {
  const prices: number[] = [
    ...new Set(
      Object.values(data)
        .filter((data) => data.price)
        .map((data) => data.price)
    ),
  ]

  const ROUNDING = 50

  const min = Math.floor(Math.min(...prices) / ROUNDING) * ROUNDING
  const max = Math.ceil(Math.max(...prices) / ROUNDING) * ROUNDING

  if (sliderEl?.noUiSlider) {
    switch (prices.length) {
      case 0:
      case 1:
        sliderEl.setAttribute('disabled', 'disabled')
        return
      default:
        sliderEl.removeAttribute('disabled')
    }

    const slider = sliderEl.noUiSlider

    let startMin = min
    let startMax = max

    if (input.value.includes(':')) {
      const [currentMin, currentMax] = input.value.split(':')

      startMin = Number(currentMin)
      startMax = Number(currentMax)
    }

    slider.updateOptions(
      {
        range: {
          min,
          max,
        },
        start: [startMin, startMax],
      },
      synchronizeInput
    )
  } else {
    // if the slider isn't initialized, update the input for a future init
    const step = input.dataset.step ? parseInt(input.dataset.step, 10) : 0

    input.setAttribute('data-min', (min > step ? min - step : 0).toString())
    input.setAttribute('data-max', max.toString())
  }
}
